import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupiahFull, formatRupiahShort, formatTanggalID, todayYMD } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { LogOut, TrendingDown, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import BottomSheet from "@/components/BottomSheet";
import { formatNominalInput, parseNominal } from "@/lib/format";

// Form pengeluaran sederhana untuk member
function PengeluaranMemberForm({ open, onClose, onSaved }) {
  const [tanggal, setTanggal] = useState(todayYMD());
  const [deskripsi, setDeskripsi] = useState("");
  const [nominal, setNominal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTanggal(todayYMD());
      setDeskripsi("");
      setNominal("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const nom = parseNominal(nominal);
    if (nom <= 0) return toast.error("Nominal harus lebih dari 0");
    if (!deskripsi.trim()) return toast.error("Deskripsi wajib diisi");
    setBusy(true);
    try {
      await api.post("/transaksi/member", {
        deskripsi: deskripsi.trim(),
        nominal: nom,
        tanggal,
      });
      toast.success("Pengeluaran tercatat");
      onClose();
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none text-sm bg-white";

  return (
    <BottomSheet open={open} onClose={onClose} title="Catat Pengeluaran">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600">Tanggal</label>
          <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600">Deskripsi</label>
          <input
            type="text"
            value={deskripsi}
            onChange={(e) => setDeskripsi(e.target.value)}
            placeholder="mis. Belanja dapur, transport, dll"
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600">Nominal (Rp)</label>
          <input
            type="text"
            inputMode="numeric"
            value={nominal}
            onChange={(e) => setNominal(formatNominalInput(e.target.value))}
            placeholder="0"
            className={inputCls}
            required
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full h-11 rounded-xl font-semibold text-sm text-white disabled:opacity-60"
          style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}
        >
          {busy ? "Menyimpan..." : "Simpan Pengeluaran"}
        </button>
      </form>
    </BottomSheet>
  );
}

export default function BerandaMember() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/member/dashboard");
      setData(res.data);
    } catch {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="p-6 text-slate-500 text-sm">Memuat dashboard...</div>
    );
  }

  const pct = data.pct_terpakai || 0;
  const barColor = pct >= 90 ? "#EF4444" : pct >= 70 ? "#EA580C" : "#2563EB";

  return (
    <div className="pb-4" style={{ background: "#F0F4FF", minHeight: "100vh" }}>

      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <div>
          <div className="text-base font-semibold" style={{ color: "#1a1a2e" }}>
            Halo, {data.name || user?.name}! 👋
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
            Kelola pengeluaranmu dengan bijak
          </div>
        </div>
        <button
          onClick={logout}
          className="w-9 h-9 rounded-full bg-white border flex items-center justify-center"
          style={{ borderColor: "#E5E7EB" }}
        >
          <LogOut size={16} style={{ color: "#6B7280" }} />
        </button>
      </div>

      {/* Kartu Jatah Bulanan */}
      <div className="px-4 mb-3">
        <div
          className="rounded-2xl p-5 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2563EB 60%, #3B82F6 100%)" }}
        >
          <div className="text-[11px] uppercase tracking-widest text-white/75 font-medium mb-1">
            Jatah bulan ini
          </div>
          <div className="text-3xl font-bold tracking-tight mb-1">
            {formatRupiahShort(data.sisa_jatah)}
          </div>
          <div className="text-xs text-white/75 mb-4">
            sisa dari {formatRupiahShort(data.jatah_bulanan)}
          </div>

          {/* Progress bar */}
          <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: "rgba(255,255,255,0.2)" }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: pct >= 90 ? "#FCA5A5" : "#34D399" }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-white/70">
            <span>Terpakai {pct}%</span>
            <span>Keluar hari ini: {formatRupiahShort(data.keluar_hari_ini || 0)}</span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="text-[10px] text-white/70">Total pengeluaran</div>
              <div className="text-sm font-bold mt-1">{formatRupiahShort(data.total_keluar)}</div>
            </div>
            <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="text-[10px] text-white/70">Sisa jatah</div>
              <div className="text-sm font-bold mt-1">{formatRupiahShort(data.sisa_jatah)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning kalau hampir habis */}
      {pct >= 80 && (
        <div className="px-4 mb-3">
          <div className="rounded-xl p-3 flex items-center gap-2" style={{ background: "#FEF2F2", border: "0.5px solid #FECACA" }}>
            <span className="text-lg">⚠️</span>
            <div className="text-xs" style={{ color: "#B91C1C" }}>
              {pct >= 90
                ? "Jatah bulan ini hampir habis! Hemat pengeluaran."
                : "Jatah bulan ini sudah 80% terpakai. Hati-hati!"}
            </div>
          </div>
        </div>
      )}

      {/* Tombol Catat Pengeluaran */}
      <div className="px-4 mb-4">
        <button
          onClick={() => setOpenForm(true)}
          className="w-full h-12 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}
        >
          <Plus size={18} />
          Catat Pengeluaran
        </button>
      </div>

      {/* Riwayat Pengeluaran */}
      <div className="px-4">
        <div className="text-sm font-bold mb-2" style={{ color: "#111827" }}>
          Riwayat pengeluaran bulan ini
        </div>
        {data.last_transaksi.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 text-center text-xs border" style={{ color: "#6B7280", borderColor: "#E5E7EB" }}>
            Belum ada pengeluaran bulan ini.
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden border" style={{ borderColor: "#F3F4F6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            {data.last_transaksi.map((t, i) => (
              <div
                key={t.id}
                className={`flex items-center justify-between px-4 py-3 ${i > 0 ? "border-t" : ""}`}
                style={{ borderColor: "#F3F4F6" }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#FEF2F2" }}>
                    <ShoppingBag size={14} style={{ color: "#EF4444" }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate" style={{ color: "#111827" }}>{t.deskripsi}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: "#9CA3AF" }}>{formatTanggalID(t.tanggal)}</div>
                  </div>
                </div>
                <div className="text-xs font-bold whitespace-nowrap ml-2" style={{ color: "#EF4444" }}>
                  -{formatRupiahFull(t.nominal)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form */}
      <PengeluaranMemberForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSaved={load}
      />
    </div>
  );
}